import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { IMovementRS, TStorageItemRS, TModuleOptions } from '../../../../models/storageModel'
import { getStockAlerts, TStockAlertRow } from '../../../../utils/reportUtils'
import { TExcelColumn } from '../../../../utils/excelExport'
import { textNumberFormater } from '../../../../utils'

const { Text } = Typography

interface StockAlertsReportProps {
  items: TStorageItemRS[]
  movements: IMovementRS[]
  categoryOptions: TModuleOptions[]
  loading?: boolean
}

export const alertsExcelColumns: TExcelColumn[] = [
  { header: 'Código', key: 'code', width: 18 },
  { header: 'Producto', key: 'name', width: 30 },
  { header: 'Categoría', key: 'category', width: 22 },
  { header: 'Existencia Actual', key: 'quantity', width: 16, format: 'number' },
  { header: 'Tipo de Alerta', key: 'alertLabel', width: 25 }
]

export function getAlertsExcelData(
  items: TStorageItemRS[],
  movements: IMovementRS[],
  categoryOptions: TModuleOptions[]
) {
  return getStockAlerts(items, movements, categoryOptions)
}

export const StockAlertsReport: React.FC<StockAlertsReportProps> = ({
  items,
  movements,
  categoryOptions,
  loading = false
}) => {
  const alertsData = getStockAlerts(items, movements, categoryOptions)

  const columns: ColumnsType<TStockAlertRow> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 130,
      render: (text) => <code>{text || '-'}</code>
    },
    {
      title: 'Producto',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      width: 160
    },
    {
      title: 'Existencia',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (val) => (
        <Text strong style={{ color: val < 0 ? '#ff4d4f' : val === 0 ? '#fa8c16' : '#1890ff' }}>
          {textNumberFormater(val)}
        </Text>
      )
    },
    {
      title: 'Alerta Detectada',
      dataIndex: 'alertType',
      key: 'alertType',
      width: 220,
      filters: [
        { text: 'Existencia Negativa', value: 'NEGATIVE' },
        { text: 'Sin Stock / Agotado', value: 'OUT_OF_STOCK' },
        { text: 'Bajo del Mínimo', value: 'BELOW_MIN' },
        { text: 'Sin Movimiento (Estancado)', value: 'STAGNANT' }
      ],
      onFilter: (value, record) => record.alertType === value,
      render: (_, record) => {
        let color = 'orange'
        if (record.alertType === 'NEGATIVE') color = 'magenta'
        if (record.alertType === 'OUT_OF_STOCK') color = 'red'
        if (record.alertType === 'BELOW_MIN') color = 'volcano'
        if (record.alertType === 'STAGNANT') color = 'gold'
        return <Tag color={color} style={{ fontSize: '0.85rem' }}>{record.alertLabel}</Tag>
      }
    }
  ]

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Table<TStockAlertRow>
        columns={columns}
        dataSource={alertsData}
        loading={loading}
        pagination={{
          defaultPageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: ['15', '30', '50'],
          showTotal: (total) => `Total ${total} alertas encontradas`
        }}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
      />
    </div>
  )
}

export default StockAlertsReport
